import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_RULES,
  builtInMaps,
  mdmToArenaMap,
  type AccountProfile,
  type ArenaMap,
  type Language,
  type LobbySettings,
  type MatchRules,
  type PlayerCount,
  type ServerMessage
} from "@marshmallow-duel/simulation";

import { ApiError, listMaps, logoutAccount, updateProfile } from "../game/api-client";
import { NetworkClient } from "../game/network-client";
import { SPLATCH_SIGN_IN_URL, clearAccountCache, loadInitialAccount } from "../game/splatch-session";
import type { GamePreferences } from "../game/preferences";
import { AbilitiesDialog } from "./AbilitiesDialog";
import { OnlineGameScreen } from "./OnlineGameScreen";
import { ScaledStage } from "./ScaledStage";

import type { TranslationKey } from "../i18n";

type RoomState = Extract<ServerMessage, { type: "roomState" }>;
type MatchedMessage = Extract<ServerMessage, { type: "matched" }>;

interface OnlineExperienceProps {
  readonly language: Language;
  readonly preferences: GamePreferences;
  readonly t: (key: TranslationKey) => string;
  readonly onBack: () => void;
}

const cssColor = (color: number): string => {
  const red = color & 0xff;
  const green = (color >> 8) & 0xff;
  const blue = (color >> 16) & 0xff;
  return `#${red.toString(16).padStart(2, "0")}${green.toString(16).padStart(2, "0")}${blue.toString(16).padStart(2, "0")}`;
};

const gmColor = (color: string): number => Number.parseInt(`${color.slice(5, 7)}${color.slice(3, 5)}${color.slice(1, 3)}`, 16);

const cloneRules = (playerCount: PlayerCount = 2): MatchRules => ({ ...DEFAULT_RULES, playerCount, abilityIds: [...DEFAULT_RULES.abilityIds] });

const SERVER_ERROR_KEYS: Readonly<Partial<Record<string, TranslationKey>>> = {
  roomNotFound: "online.error.roomNotFound",
  roomFull: "online.error.roomFull",
  playersNotReady: "online.error.playersNotReady",
  hostOnly: "online.error.hostOnly",
  serverFull: "online.error.serverFull",
  invalidSettings: "online.error.invalidSettings",
  alreadyInRoom: "online.error.alreadyInRoom",
  rateLimited: "online.error.rateLimited"
};

const serverErrorKey = (code: string): TranslationKey => SERVER_ERROR_KEYS[code] ?? "online.error.generic";

export const OnlineExperience = ({ language, preferences, t, onBack }: OnlineExperienceProps) => {
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [network, setNetwork] = useState<NetworkClient | null>(null);
  const [ownPeerId, setOwnPeerId] = useState("");
  const [room, setRoom] = useState<RoomState | null>(null);
  const [match, setMatch] = useState<MatchedMessage | null>(null);
  const [error, setError] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [rules, setRules] = useState<MatchRules>(() => cloneRules());
  const [selectedMap, setSelectedMap] = useState<ArenaMap>(() => builtInMaps(2)[0]!);
  const [communityMaps, setCommunityMaps] = useState<readonly ArenaMap[]>([]);
  const [showAbilities, setShowAbilities] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState({ name: "", color: 255, skin: 1 });
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let active = true;
    void loadInitialAccount().then((profile) => {
      if (!active) return;
      setAccount(profile);
      if (profile) setProfileDraft({ name: profile.name, color: profile.color, skin: profile.skin });
    }).catch(() => active && setAuthError(true)).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!account) return;
    const client = new NetworkClient(language, account);
    const unsubscribe = client.subscribe((message) => {
      if (message.type === "welcome") setOwnPeerId(message.peerId);
      else if (message.type === "roomState") {
        setRoom(message);
        setRules(message.settings.rules);
        setSelectedMap(message.settings.map);
        setError("");
      } else if (message.type === "matched") setMatch(message);
      else if (message.type === "peerLeft" && message.hostLeft) {
        setRoom(null);
        setMatch(null);
        setError(t("online.hostLeft"));
      } else if (message.type === "error") setError(t(serverErrorKey(message.code)));
    });
    client.onDisconnected = () => setError(t("online.disconnected"));
    setNetwork(client);
    return () => {
      unsubscribe();
      client.close();
      setNetwork(null);
    };
  }, [account?.id, language, t]);

  useEffect(() => {
    let active = true;
    void listMaps({ playerCount: rules.playerCount }).then((records) => {
      if (!active) return;
      setCommunityMaps(records.map((record) => mdmToArenaMap(record.map, `library:${record.id}`)));
    }).catch(() => active && setCommunityMaps([]));
    return () => { active = false; };
  }, [rules.playerCount]);

  const maps = useMemo(() => [...builtInMaps(rules.playerCount), ...communityMaps.filter(({ playerCount }) => playerCount === rules.playerCount)], [communityMaps, rules.playerCount]);

  const settings = (nextRules = rules, nextMap = selectedMap): LobbySettings => ({
    maxPlayers: nextRules.playerCount,
    mapId: nextMap.id,
    map: nextMap,
    rules: nextRules
  });

  const changePlayerCount = (count: PlayerCount, updateRoom = false) => {
    const nextRules = { ...rules, playerCount: count };
    const nextMap = builtInMaps(count)[0]!;
    setRules(nextRules);
    setSelectedMap(nextMap);
    if (updateRoom) network?.updateSettings(settings(nextRules, nextMap));
  };

  const changeRules = (nextRules: MatchRules) => {
    setRules(nextRules);
    if (room && ownPeerId === room.hostPeerId) network?.updateSettings(settings(nextRules));
  };

  const changeMap = (id: string) => {
    const map = maps.find((candidate) => candidate.id === id);
    if (!map) return;
    setSelectedMap(map);
    if (room && ownPeerId === room.hostPeerId) network?.updateSettings(settings(rules, map));
  };

  const saveProfile = async () => {
    setWorking(true);
    setError("");
    try {
      const updated = await updateProfile(profileDraft);
      setAccount(updated);
      network?.updateProfile(updated);
      setEditingProfile(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.code : "internalError");
    } finally {
      setWorking(false);
    }
  };

  const logout = async () => {
    setWorking(true);
    try {
      await logoutAccount();
      clearAccountCache();
      setAccount(null);
      setRoom(null);
      setMatch(null);
    } finally {
      setWorking(false);
    }
  };

  if (match && network) return <OnlineGameScreen match={match} network={network} preferences={preferences} t={t} onLeave={() => { setMatch(null); setRoom(null); }} />;

  if (loading) {
    return <ScaledStage className="mode-stage"><div className="online-shell online-centered"><strong>{t("online.loading")}</strong></div><div className="menu-water" /></ScaledStage>;
  }

  if (!account) {
    return (
      <ScaledStage className="mode-stage">
        <img className="mode-title-image" src="/assets/original/backgrounds/bg_menu_title.png" alt="Marshmallow Duel Reloaded" />
        <section className="online-shell splatch-login">
          <span className="splatch-mark">S</span>
          <h1>{t("online.splatchTitle")}</h1>
          <p>{t("online.splatchRequired")}</p>
          {authError && <div className="online-error">{t("online.splatchFailed")}</div>}
          <a className="online-primary" href={SPLATCH_SIGN_IN_URL}>{t("online.continueSplatch")}</a>
          <button type="button" className="original-text-button" onClick={onBack}>{t("common.back")}</button>
        </section>
        <div className="menu-water" />
      </ScaledStage>
    );
  }

  if (room) {
    const isHost = ownPeerId === room.hostPeerId;
    const self = room.players.find(({ peerId }) => peerId === ownPeerId);
    const allReady = room.players.length === room.settings.maxPlayers && room.players.every(({ ready }) => ready);
    return (
      <ScaledStage className="mode-stage">
        <img className="mode-title-image" src="/assets/original/backgrounds/bg_menu_title.png" alt="Marshmallow Duel Reloaded" />
        <section className="online-shell online-lobby">
          <header><div><small>{t("online.roomCode")}</small><strong>{room.code}</strong></div><span>{room.players.length}/{room.settings.maxPlayers}</span></header>
          <div className="online-lobby-grid">
            <div className="online-player-list">
              {Array.from({ length: room.settings.maxPlayers }, (_, index) => {
                const slot = index + 1;
                const player = room.players.find((candidate) => candidate.slot === slot);
                return <div key={slot} className={player ? "occupied" : ""}><b>P{slot}</b><span>{player?.profile.name ?? t("online.waiting")}</span><i>{player?.ready ? "✓" : ""}</i></div>;
              })}
            </div>
            <div className="online-host-settings">
              <label>{t("settings.players")}<select disabled={!isHost || room.players.length > 2} value={room.settings.maxPlayers} onChange={(event) => changePlayerCount(Number(event.target.value) as PlayerCount, true)}><option value={2}>2</option><option value={3}>3</option><option value={4}>4</option></select></label>
              <label>{t("maps.title")}<select disabled={!isHost} value={selectedMap.id} onChange={(event) => changeMap(event.target.value)}>{maps.map((map) => <option key={map.id} value={map.id}>{map.name}</option>)}</select></label>
              <label>{t("settings.abilitiesCount")}<input disabled={!isHost} type="number" min={0} max={48} value={rules.abilitiesCount} onChange={(event) => changeRules({ ...rules, abilitiesCount: Math.max(0, Math.min(48, Number(event.target.value))) })} /></label>
              <label>{t("settings.winnerPoints")}<input disabled={!isHost} type="number" min={1} max={99} value={rules.winnerPoints} onChange={(event) => changeRules({ ...rules, winnerPoints: Math.max(1, Math.min(99, Number(event.target.value))) })} /></label>
              {isHost && <button type="button" onClick={() => setShowAbilities(true)}>{t("abilities.title")}</button>}
              {(["ropes", "shields", "physics"] as const).map((key) => <label key={key} className="online-check"><input disabled={!isHost} type="checkbox" checked={rules[key]} onChange={(event) => changeRules({ ...rules, [key]: event.target.checked })} />{t(`settings.${key}`)}</label>)}
            </div>
          </div>
          {error && <div className="online-error">{error}</div>}
          <footer>
            <button type="button" onClick={() => { network?.leaveRoom(); setRoom(null); }}>{t("common.back")}</button>
            {isHost ? <button className="online-primary" type="button" disabled={!allReady} onClick={() => network?.startRoom()}>{t("online.start")}</button> : <button className={self?.ready ? "online-primary ready" : "online-primary"} type="button" onClick={() => network?.setReady(!self?.ready)}>{t("common.ready")}</button>}
          </footer>
        </section>
        {showAbilities && <AbilitiesDialog language={language} rules={rules} t={t} onChange={changeRules} onClose={() => setShowAbilities(false)} />}
        <div className="menu-water" />
      </ScaledStage>
    );
  }

  return (
    <ScaledStage className="mode-stage">
      <img className="mode-title-image" src="/assets/original/backgrounds/bg_menu_title.png" alt="Marshmallow Duel Reloaded" />
      <section className="online-shell online-home">
        <header><div><small>Splatch</small><strong>{account.username}</strong></div><button type="button" onClick={() => setEditingProfile((value) => !value)}>{t("online.profile")}</button></header>
        {editingProfile ? (
          <div className="online-profile-editor">
            <img src={`/assets/original/sprites/spr_player_${profileDraft.skin}_standing/0.png`} alt="" style={{ filter: `drop-shadow(0 0 6px ${cssColor(profileDraft.color)})` }} />
            <label>{t("players.name")}<input maxLength={20} value={profileDraft.name} onChange={(event) => setProfileDraft({ ...profileDraft, name: event.target.value })} /></label>
            <label>{t("players.color")}<input type="color" value={cssColor(profileDraft.color)} onChange={(event) => setProfileDraft({ ...profileDraft, color: gmColor(event.target.value) })} /></label>
            <label>{t("players.skin")}<select value={profileDraft.skin} onChange={(event) => setProfileDraft({ ...profileDraft, skin: Number(event.target.value) })}>{[1, 2, 3, 4].map((skin) => <option key={skin}>{skin}</option>)}</select></label>
            <div><button type="button" disabled={working} onClick={() => setEditingProfile(false)}>{t("common.cancel")}</button><button type="button" disabled={working} onClick={() => void saveProfile()}>{t("common.save")}</button></div>
          </div>
        ) : (
          <>
            <div className="online-create-settings">
              <label>{t("settings.players")}<select value={rules.playerCount} onChange={(event) => changePlayerCount(Number(event.target.value) as PlayerCount)}><option value={2}>2</option><option value={3}>3</option><option value={4}>4</option></select></label>
              <label>{t("maps.title")}<select value={selectedMap.id} onChange={(event) => changeMap(event.target.value)}>{maps.map((map) => <option key={map.id} value={map.id}>{map.name}</option>)}</select></label>
              <button className="online-primary" type="button" onClick={() => network?.createRoom(settings())}>{t("online.create")}</button>
            </div>
            <div className="online-join"><input value={joinCode} maxLength={6} placeholder={t("online.codePlaceholder")} onChange={(event) => setJoinCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} /><button type="button" disabled={joinCode.length !== 6} onClick={() => network?.joinRoom(joinCode)}>{t("online.join")}</button></div>
          </>
        )}
        {error && <div className="online-error">{error}</div>}
        <footer><button type="button" onClick={onBack}>{t("common.back")}</button><button type="button" disabled={working} onClick={() => void logout()}>{t("online.signOut")}</button></footer>
      </section>
      <div className="menu-water" />
    </ScaledStage>
  );
};
